import { useEffect, useMemo, useRef } from 'react';

import * as endpoints from 'utils/api/endpoints';
import { FindPlayersPhotoResponse } from 'utils/api/dto';
import { httpMethods, useFetchData } from 'utils/hooks/use-fetch-data';

const NOT_FOUND = 404;

export type UseEliteProspectPhotosRequestParams = {
    /** Elite Prospects player ids; a new array with the same ids does not repeat the request */
    ids: ReadonlyArray<number>;
    /**
     * The answer for the ids of the request: the photos, or none when the backend has none of them (404). Not called
     * for a request cancelled because the ids changed, nor for other failures (they are tried again with other ids).
     */
    onLoaded?: (ids: number[], photos: FindPlayersPhotoResponse[]) => void;
};

const toIds = (idsKey: string) => (idsKey ? idsKey.split(',').map(Number) : []);

/**
 * One request for the Elite Prospects photos of the given players
 * (/api/v2/elite-prospects/get-players-photo; 404 when none of them has a photo — useFetchData logs it).
 * The callers parse `data` by their own rules, or take each answer with onLoaded. `data` is null while there are no ids.
 */
export const useEliteProspectPhotosRequest = ({ ids, onLoaded }: UseEliteProspectPhotosRequestParams) => {
    // A string: callers often pass a new array with the same ids
    const idsKey = Array.from(new Set(ids)).join(',');
    const photoUrl = useMemo(() => (idsKey ? endpoints.getEliteProspectsPlayersPhoto(idsKey.split(',')) : null), [
        idsKey
    ]);
    // The ids of the request in flight: an answer belongs to them (useFetchData calls the latest handlers)
    const requestedIdsRef = useRef(toIds(idsKey));

    const { data, processing, handleRepeat, handleCancel } = useFetchData<FindPlayersPhotoResponse[]>({
        method: httpMethods.get,
        url: photoUrl ?? undefined,
        initialCondition: Boolean(photoUrl),
        responseHandler: photos => onLoaded?.(requestedIdsRef.current, photos ?? []),
        onRequestError: error => {
            const status = (error as { response?: { status?: number } } | null)?.response?.status;

            if (status === NOT_FOUND) onLoaded?.(requestedIdsRef.current, []);
        }
    });

    // useFetchData does not follow a new url by itself: null -> url starts with initialCondition,
    // url -> other url and url -> null (no ids any more) are handled here
    const previousPhotoUrlRef = useRef(photoUrl);

    useEffect(() => {
        const previousPhotoUrl = previousPhotoUrlRef.current;
        previousPhotoUrlRef.current = photoUrl;
        requestedIdsRef.current = toIds(idsKey);

        if (!previousPhotoUrl || previousPhotoUrl === photoUrl) {
            return;
        }

        if (photoUrl) {
            handleRepeat();
        } else {
            handleCancel();
        }
    }, [photoUrl, idsKey, handleRepeat, handleCancel]);

    return { data: photoUrl ? data : null, processing: Boolean(photoUrl) && processing };
};
